import { useLayoutEffect, useState } from 'react';
import { debounce } from 'lodash-es';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';
import { withWindow } from '@shared/utils/withWindow';

type ScaleBy = 'fit' | 'width';

function calcFitScale(scaleBy: ScaleBy, parentwidth: number, parentheight: number, childwidth: number, childheight: number) {
  if (scaleBy === 'width') return parentwidth / childwidth;

  const parentRatio = parentwidth / parentheight;
  const childRatio = childwidth / childheight;
  return parentRatio < childRatio ? parentwidth / childwidth : parentheight / childheight;
}

export function useScaleToContainer<T extends HTMLElement>(padding = 0, scaleBy: ScaleBy = 'fit') {
  const [container, setContainer] = useState<T | null>(null);
  const [decorator, setDecorator] = useState<T | null>(null);
  const [content, setContent] = useState<T | null>(null);

  useLayoutEffect(() => {
    if (!container) return;
    if (!decorator) return;
    if (!content) return;

    let lastSizeKey: string | undefined;

    const measure = () => {
      // Measure with getBoundingClientRect() (subpixel) rather than clientWidth/clientHeight
      // (integer), which drifts under zoom. `content.*` stays on client* on purpose: it is the
      // element we scale, so its layout box is transform-independent and exact (inches * PPI).
      // All layout reads come before the first style write, so no read forces a synchronous reflow.
      const rect = container.getBoundingClientRect();
      const contentWidth = content.clientWidth;
      const contentHeight = content.clientHeight;

      const sizeKey = `${rect.width}x${rect.height}:${contentWidth}x${contentHeight}`;
      if (sizeKey === lastSizeKey) return;
      lastSizeKey = sizeKey;

      // For square products such as napkins, there ends up being no padding, and feels tight.
      // So we are compensating for that here:
      const isSquare = contentWidth === contentHeight;
      const adjustment = isSquare ? 0.8 : 1;

      // prettier-ignore
      const zoom = adjustment * calcFitScale(
        scaleBy,
        Math.max(0, rect.width - padding * 2),
        Math.max(0, rect.height - padding * 2),
        contentWidth,
        contentHeight,
      );
      decorator.style.width = `${contentWidth * zoom}px`;
      decorator.style.height = `${contentHeight * zoom}px`;

      // Correct the centering
      content.style.position = 'relative';
      content.style.top = `${(contentHeight * zoom - contentHeight) / 2}px`;
      content.style.left = `${(contentWidth * zoom - contentWidth) / 2}px`;
      content.style.scale = `${zoom}`;
    };

    // Scale before first paint. The synchronous first delivery then only catches a size that changed
    // since (e.g. a grid track resolving), and the size guard makes it a no-op otherwise.
    measure();
    const resizeObserver = createSafeResizeObserver(() => measure(), { syncInitialDelivery: true });
    resizeObserver.observe(container);

    // Browser zoom (⌘ +/-) changes devicePixelRatio but does NOT reliably deliver a settled
    // ResizeObserver entry in Safari — so a fast overshoot/snap-back leaves each tile latched
    // to a scale computed mid-zoom, and they never reconverge until a reload. Re-measure on the
    // zoom/resize signals too, debounced so the final (settled) reading wins across all tiles.
    // Routed through `withWindow` to stay SSR-safe.
    const cleanupWindowListeners = withWindow(
      win => {
        const remeasure = debounce(() => measure(), 150);
        let resolutionQuery: MediaQueryList | null = null;

        // A `(resolution: <dpr>dppx)` media query matches only the exact ratio it was created
        // with, so after each zoom step we tear it down and re-arm one for the new dpr.
        // Feature-detect addEventListener — Safari < 14 only supports the deprecated addListener.
        function onResolutionChange() {
          remeasure();
          stopWatchingResolution();
          watchResolution();
        }
        function watchResolution() {
          if (typeof win.matchMedia !== 'function') return;
          resolutionQuery = win.matchMedia(`(resolution: ${win.devicePixelRatio}dppx)`);
          if (resolutionQuery.addEventListener) resolutionQuery.addEventListener('change', onResolutionChange);
          else resolutionQuery.addListener(onResolutionChange);
        }
        function stopWatchingResolution() {
          if (!resolutionQuery) return;
          if (resolutionQuery.removeEventListener) resolutionQuery.removeEventListener('change', onResolutionChange);
          else resolutionQuery.removeListener(onResolutionChange);
          resolutionQuery = null;
        }

        watchResolution();
        win.addEventListener('resize', remeasure);
        win.visualViewport?.addEventListener('resize', remeasure);

        return () => {
          remeasure.cancel();
          stopWatchingResolution();
          win.removeEventListener('resize', remeasure);
          win.visualViewport?.removeEventListener('resize', remeasure);
        };
        // No-op default so SSR (no window) yields a callable cleanup rather than `void`.
      },
      () => {},
    );

    return () => {
      resizeObserver.disconnect();
      cleanupWindowListeners();
    };
  }, [container, decorator, content, padding, scaleBy]);

  // prettier-ignore
  return [
    (container: T) => setContainer(container),
    (decorator: T) => setDecorator(decorator),
    (content: T) => setContent(content)
  ] as const;
}
